import React, { useState, useEffect, useCallback } from 'react';
import { useSelector } from 'react-redux';
import { toast } from 'react-toastify';
import config from '../../config';

function CallToLead({ leadId, leadFieldPermissions }) {
  // Call log add permission
  const hasAddPermission = !!leadFieldPermissions?.call_log?.add;
  const userInfo = useSelector((state) => state.userInfo);
  const loggedInUserName = userInfo?.name || '';
  const loggedInId = userInfo?.id || '';

  const [mobile, setMobile] = useState('');
  const [leadName, setLeadName] = useState('');
  const [callOutcome, setCallOutcome] = useState('');
  const [comment, setComment] = useState('');
  const [callLogs, setCallLogs] = useState([]);
  const [loading, setLoading] = useState(false);
  const [outcomesLoading, setOutcomesLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [callDateTime, setCallDateTime] = useState('');
  const [callOutcomes, setCallOutcomes] = useState([]);

  // Default value in datetime-local format (YYYY-MM-DDTHH:mm), current time
  const getDefaultDateTime = () => {
    const now = new Date();
    const pad = (n) => String(n).padStart(2, '0');
    return `${now.getFullYear()}-${pad(now.getMonth() + 1)}-${pad(now.getDate())}T${pad(
      now.getHours()
    )}:${pad(now.getMinutes())}`;
  };

  const fetchCallOutcomes = useCallback(() => {
    setOutcomesLoading(true);
    fetch(`${config.API_BASE_URL}get_lead_call_outcomes`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
    })
      .then((res) => res.json())
      .then((result) => {
        setOutcomesLoading(false);
        console.log('get_lead_call_outcomes response:', result);

        if (result?.success === 1) {
          setCallOutcomes(result.data || []);
        } else {
          console.warn(
            'get_lead_call_outcomes failed:',
            result?.message
          );
        }
      })
      .catch((err) => {
        setOutcomesLoading(false);
        console.error('get_lead_call_outcomes error:', err);
      });
  }, []);

  const fetchCallLogs = useCallback(() => {
    if (!leadId) {
      console.warn('CallToLead: leadId is missing, skipping fetchCallLogs');
      return;
    }
    setLoading(true);

    fetch(`${config.API_BASE_URL}get_lead_call_logs`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ lead_id: leadId }),
    })
      .then((res) => res.json())
      .then((result) => {
        setLoading(false);
        console.log('get_lead_call_logs response:', result);
        if (result?.success === 1) {
          setMobile(result.data?.mobile || '');
          setLeadName(result.data?.lead_name || '');
          setCallLogs(result.data?.call_logs || []);
        } else {
          console.warn('get_lead_call_logs failed:', result?.message);
        }
      })
      .catch((err) => {
        setLoading(false);
        console.error('get_lead_call_logs error:', err);
      });
  }, [leadId]);

  useEffect(() => {
    fetchCallLogs();
    fetchCallOutcomes();
    setCallDateTime(getDefaultDateTime());
  }, [fetchCallLogs, fetchCallOutcomes]);

  const handleSubmit = (e) => {
    e.preventDefault();

    if (!leadId) {
      toast.error('Lead ID is missing.', { position: 'top-right' });
      return;
    }
    if (!mobile) {
      toast.error('Mobile number is required.', { position: 'top-right' });
      return;
    }
    if (!callOutcome) {
      toast.error('Please select call outcome.', { position: 'top-right' });
      return;
    }
    if (!callDateTime) {
      toast.error('Please select date & time.', { position: 'top-right' });
      return;
    }

    setSubmitting(true);

    const payload = {
      lead_id: leadId,
      user_id: loggedInId,
      mobile,
      dialed_by_name: loggedInUserName,
      call_outcome: callOutcome,
      comment,
      call_datetime: callDateTime,
    };

    console.log('save_lead_call_log payload:', payload);

    fetch(`${config.API_BASE_URL}save_lead_call_log`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(payload),
    })
      .then((res) => res.json())
      .then((result) => {
        setSubmitting(false);
        console.log('save_lead_call_log response:', result);
        if (result?.success === 1) {
          toast.success('Call log saved successfully.', { position: 'top-right' });
          setCallOutcome('');
          setComment('');
          setCallDateTime(getDefaultDateTime());
          fetchCallLogs();
        } else {
          toast.error(result?.message || 'Something went wrong.', { position: 'top-right' });
        }
      })
      .catch((err) => {
        setSubmitting(false);
        console.error('save_lead_call_log error:', err);
        toast.error('Something went wrong. Please try again.', { position: 'top-right' });
      });
  };

  const initialLoading = loading || outcomesLoading;

  if (initialLoading) {
    return (
      <div className="text-center py-5">
        <div className="spinner-border text-primary" role="status">
          <span className="visually-hidden">Loading...</span>
        </div>
      </div>
    );
  }

  return (
    <div className="row">
      <div className="col-md-6">
        <h5 className="fw-bold mb-3">Log a Call {leadName ? `- ${leadName}` : ''}</h5>
        <form onSubmit={handleSubmit}>
          <input type="hidden" name="dialed_by_name" value={loggedInUserName} />

          <div className="form-group mb-3">
            <label htmlFor="mobile">
              <span>Mobile Number</span>
              <input
                type="tel"
                id="mobile"
                name="mobile"
                className="form-control"
                value={mobile}
                onChange={(e) => setMobile(e.target.value)}
              />
            </label>
          </div>

          <div className="mb-3">
            <span
              style={{
                display: 'inline-block',
                background: '#eef7ff',
                color: '#0d6efd',
                padding: '5px 12px',
                borderRadius: '20px',
                fontSize: '13px',
                fontWeight: 500,
              }}
            >
              <i className="bi bi-telephone-fill me-1" /> Dialed by: {loggedInUserName || 'N/A'}
            </span>
          </div>

          <div className="form-group mb-3">
            <label htmlFor="call_datetime">
              <span>Date &amp; Time</span>
              <input
                type="datetime-local"
                id="call_datetime"
                className="form-control"
                value={callDateTime}
                onChange={(e) => setCallDateTime(e.target.value)}
              />
            </label>
          </div>

          <div className="form-group mb-3">
            <label htmlFor="call_outcome">
              <span>
                Call Outcome <a style={{ color: 'red' }}>* </a>
              </span>
              <select
                id="call_outcome"
                className="form-select"
                value={callOutcome}
                onChange={(e) => setCallOutcome(e.target.value)}
                >
                <option value="">Select Outcome</option>

                {callOutcomes.map((outcome) => (
                    <option
                    key={outcome.id}
                    value={outcome.outcome_name}
                    >
                    {outcome.outcome_name}
                    </option>
                ))}
                </select>
            </label>
          </div>

          <div className="form-group mb-3">
            <label htmlFor="comment">
              <span>Comment</span>
              <textarea
                id="comment"
                className="form-control"
                rows="4"
                value={comment}
                onChange={(e) => setComment(e.target.value)}
              />
            </label>
          </div>

          {hasAddPermission && (
          <button type="submit" className="btn btn-primary" disabled={submitting}>
            {submitting ? 'Saving...' : 'Submit'}
          </button>
          )}
        </form>
      </div>

      <div className="col-md-6">
        <h5 className="fw-bold mb-3">Call History</h5>
        {callLogs.length === 0 ? (
          <p>No call logs found.</p>
        ) : (
          <div
            className="lead-activity"
            style={{
              maxHeight: '500px',
              overflowY: 'auto',
              position: 'relative',
              paddingLeft: '24px',
            }}
          >
            {/* vertical timeline line */}
            <div
              style={{
                position: 'absolute',
                left: '5px',
                top: '6px',
                bottom: '6px',
                width: '2px',
                background: '#d9e3f0',
              }}
            />

            {callLogs.map((log, index) => (
              <div
                key={log.id}
                className="lead-activity-item mb-4"
                style={{ position: 'relative' }}
              >
                {/* timeline dot */}
                <span
                  style={{
                    position: 'absolute',
                    left: '-24px',
                    top: '4px',
                    width: '11px',
                    height: '11px',
                    borderRadius: '50%',
                    background: '#4a90e2',
                    border: '2px solid #fff',
                    boxShadow: '0 0 0 1px #4a90e2',
                  }}
                />

                <p className="mb-1 fw-bold">
                  {log.call_outcome} <span className="badge text-bg-secondary ms-1">{log.mobile}</span>
                </p>
                <p className="mb-1" style={{ fontSize: '13px', color: '#6c757d' }}>
                  {log.created_date_formatted}
                </p>
                <p className="mb-1">
                  <b>Dialed By:</b> {log.dialed_by_name || log.createdByName}
                </p>
                {log.comment ? (
                  <p className="mb-0">
                    <b>Comment:</b> {log.comment}
                  </p>
                ) : null}
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

export default CallToLead;